import {
  AddressDTO,
  AddressType,
  ContactReferenceDTO,
  ContactType,
  ContactDTO,
  MessageResult,
  MultiStoreDTO
} from "@/model/data.model";
import i18n, { hasTranslation } from "@/lang/i18n";
import { endOfMonth, format, startOfMonth } from "date-fns";
import store from "@/vuex/store";
import { CurrentLogin } from "@/model/component.model";

export function elDatePickerFormat() {
  let format = "dd/MM/yyyy";
  if (isGermany()) {
    format = "dd.MM.yyyy";
  }

  return format;
}

export function extractAddress(addresses: AddressDTO[], type: AddressType) {
  for (let i = 0; i < addresses.length; i++) {
    if (addresses[i].type === type) {
      return addresses[i];
    }
  }
  return {
    address: "",
    city: "",
    province: "",
    zipCode: "",
    countryName: ""
  } as AddressDTO;
}

/**
 * Scans the array searching for the element with 'type' equal to
 * the parameter provided
 * @param {ContactReferenceDTO[]} contacts array in which search the element
 * @param {ContactType} type the type of element searched
 * @returns {ContactReferenceDTO} the reference to the element if found, or a new element if
 * it is not found
 */
export function extractContact(
  contacts: ContactReferenceDTO[],
  type: ContactType
) {
  for (let i = 0; i < contacts.length; i++) {
    if (contacts[i].type === type) {
      return contacts[i];
    }
  }
  const aNewItem = {
    type: type,
    value: ""
  } as ContactReferenceDTO;
  contacts.push(aNewItem);
  return aNewItem;
}

export function testContactForUpdate(contact: ContactDTO) {
  if (contact.id == null) {
    console.log(JSON.stringify(contact, null, 2));
    throw Error("ContactDTO requires a id value");
  }

  if (contact.addresses[0].id == null) {
    console.log(JSON.stringify(contact, null, 2));
    throw Error("ContactDTO AddressDTO first field requires a id value");
  }

  for (let index = 0; index < contact.contactsReference.length; index++) {
    if (contact.contactsReference[index].id == null) {
      console.log(JSON.stringify(contact), null, 2);
      throw Error("ContactDTO contactsReference require a id value");
    }
  }
}

/*
 * Builders
 */
export function buildContact() {
  return {
    addresses: [
      {
        type: "OPERATION",
        countryName: "",
        zipCode: "",
        province: "",
        address: "",
        city: "",
        state: "",
        countryId: ""
      } as AddressDTO
    ],
    contactsReference: [
      {
        type: "EMAIL",
        value: ""
      },
      {
        type: "TELEPHONE",
        value: ""
      },
      {
        type: "FAX",
        value: ""
      }
    ] as ContactReferenceDTO[],
    firstname: "",
    lastname: "",
    gender: ""
  } as ContactDTO;
}

export function isItaly() {
  return store.state.currentLogin.isItaly;
}

export function isSpain() {
  return store.state.currentLogin.isSpain;
}

export function isGermany() {
  return store.state.currentLogin.isGermany;
}

export function isAustria() {
  return store.state.currentLogin.isAustria;
}

export function isFrance() {
  return store.state.currentLogin.isFrance;
}

export function isPoland() {
  return store.state.currentLogin.isPoland;
}

export function isPortugal() {
  return store.state.currentLogin.isPortugal;
}

export function isSwitzerland() {
  return store.state.currentLogin.isSwitzerland;
}

export function isCroatia() {
  return store.state.currentLogin.isCroatia;
}

export function isUk() {
  return store.state.currentLogin.isUk;
}

export function getDatePickerOptions() {
  const date = new Date();
  return {
    shortcuts: [
      {
        text: i18n.t("today"),
        onClick(picker: any) {
          picker.$emit("pick", new Date());
        }
      },
      {
        text: i18n.t("first_day_month"),
        onClick(picker: any) {
          picker.$emit("pick", getMonthStart(date));
        }
      },
      {
        text: i18n.t("last_day_month"),
        onClick(picker: any) {
          picker.$emit("pick", getMonthEnd(date));
        }
      }
    ],
    firstDayOfWeek: 1
  };
}

export function getMonthStart(date: Date) {
  return startOfMonth(date);
}

export function getMonthEnd(date: Date) {
  return endOfMonth(date);
}

/**
 * Looukup backend error codes ( if any) from i18n
 * if unexpected or missing label print backend description
 *
 * @param reason
 */
export function translateErrorMessage(reason: any): string {
  let message = String(i18n.t("UNEXPECTED_ERROR"));
  let objectErr = null;
  if (reason?.response?.data) {
    objectErr =
      reason?.response.data.length > 0
        ? reason?.response.data[0]
        : reason?.response.data;
  }
  if (objectErr && objectErr.code) {
    if (hasTranslation(objectErr.code)) {
      message = String(i18n.t(objectErr.code));
    } else {
      if (objectErr.description) {
        message += " " + objectErr.description;
      }
      if (objectErr.message) {
        if (objectErr.message === "Invalid Input") {
          message += " <br>" + i18n.t("generic_shipment_form_error");
        } else {
          message += ' "' + objectErr.message + '"';
        }
      }
    }
  }
  return message;
}

export function getLanguageFromTenant(tenantId: number) {
  switch (Number(tenantId)) {
    case 1:
      return "it";
    case 2:
      return "es";
    case 3:
    case 4:
    case 8:
      return "de";
    case 5:
      return "fr";
    case 6:
      return "pl";
    case 7:
      return "pt";
  }
  return "en";
}

export function getLanguageId(tenantId: number) {
  switch (tenantId) {
    case 1:
      return "IT";
    case 2:
      return "ES";
    case 3:
      return "DE";
    case 4:
      return "AT";
    case 5:
      return "FR";
    case 6:
      return "PL";
    case 7:
      return "PT";
    case 8:
      return "CH";
  }
  return "";
}

export function alertDangerReason(
  reason: any,
  env: any,
  extraMessage?: string
) {
  const prefix = extraMessage ? extraMessage + " " : "";
  //alert danger for services error
  if (reason.response && reason.response.data && reason.response.data.code) {
    env.$alerts.danger(prefix + String(i18n.t(reason.response.data.code)));
  } else {
    if (
      reason.response &&
      reason.response.data &&
      reason.response.data[0] &&
      reason.response.data[0].code
    ) {
      if (
        reason.response.data[0].message &&
        reason.response.data[0].code ===
          String(i18n.t(reason.response.data[0].code))
      ) {
        env.$alerts.danger(
          prefix + String(i18n.t(reason.response.data[0].message))
        ); // Fallback message in case that the code is not in i18n
      } else {
        env.$alerts.danger(
          prefix + String(i18n.t(reason.response.data[0].code))
        );
      }
    } else {
      env.$alerts.danger(prefix + reason.message);
    }
  }
  return Promise.reject(reason.message);
}

export function fieldSorter(fields: any) {
  return function(a: any, b: any) {
    return fields
      .map(function(o: any) {
        let dir = 1;
        if (o[0] === "-") {
          dir = -1;
          o = o.substring(1);
        }
        if (typeof a[o] === "string" && typeof (b[o] === "string")) {
          if (String(a[o]).toLowerCase() > String(b[o]).toLowerCase())
            return dir;
          if (String(a[o]).toLowerCase() < String(b[o]).toLowerCase())
            return -dir;
          return 0;
        } else {
          if (a[o] > b[o]) return dir;
          if (a[o] < b[o]) return -dir;
          return 0;
        }
      })
      .reduce(function firstNonZeroValue(p: any, n: any) {
        return p ? p : n;
      }, 0);
  };
}

export function formatTypeToMime(formatType: string) {
  let mime = "text/plain";
  if (formatType === "PDF") {
    mime = "application/pdf";
  } else if (formatType === "GIF") {
    mime = "image/gif";
  } else if (formatType === "JPEG" || formatType === "JPG") {
    mime = "image/jpeg";
  } else if (formatType === "PNG") {
    mime = "image/png";
  } else if (formatType === "TIF" || formatType === "TIFF") {
    mime = "image/tiff";
  } else if (formatType === "HTML") {
    mime = "text/html";
  } else if (formatType === "XML") {
    mime = "text/xml";
  } else if (formatType === "XLSX") {
    mime = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
  } else if (formatType === "ZIP") {
    mime = "application/octet-stream";
  }
  return mime;
}

export function notifyErrorReason(
  reason: any,
  env: any,
  extraMessage?: string
) {
  const prefix = extraMessage ? extraMessage + " " : "";
  //notify danger for services error
  if (reason.response && reason.response.data && reason.response.data.code) {
    env.$notify.error(prefix + String(i18n.t(reason.response.data.code)));
  } else {
    env.$notify.error(prefix + reason.message);
  }
  return Promise.reject(reason.message);
}

export function convertBase64toBlob(content: any, contentType: any): Blob {
  contentType = contentType || "";
  const sliceSize = 512;
  const byteCharacters = window.atob(content); //method which converts base64 to binary
  const byteArrays = [];
  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize);
    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }
  const blob = new Blob(byteArrays, {
    type: contentType
  }); //statement which creates the blob
  return blob;
}

/**
 * central method to display a binary file to popup
 * @param base64 the binary from backend
 * @param formatType see supported types formatTypeToMime
 * @param fileName download file name / window title
 * @param env
 */
export function popupFileBase64(
  base64: any,
  formatType: string,
  fileName: string,
  env: any
) {
  const mime: string = formatTypeToMime(formatType);

  const blob = convertBase64toBlob(base64, mime);
  const fileURL = URL.createObjectURL(blob);

  const a: any = document.createElement("A");

  a.href = fileURL;
  a.setAttribute("target", "_blank");

  a.download = fileName;

  document.body.appendChild(a);

  // if Internet Explorer or Edge is used, save the converted PDF as blob
  if (window.navigator && window.navigator.msSaveOrOpenBlob) {
    window.navigator.msSaveOrOpenBlob(blob, a.download);
  } else {
    // for all other web browsers, display the converted PDF as iframe

    const popWindow = window.open(
      "about:blank",
      "_blank",
      "menubar=no,status=no,titlebar=no,toolbar=no,fullscreen=no,width=1024,height=768,scrollbars=1,resizable=1,"
    ) as Window;
    if (!popWindow) {
      console.error("Please enable popups from this site to see the pdf");
      env.$notify.error("Please enable popups from this site to see the pdf");
      return;
    }

    popWindow.document.title = fileName;

    popWindow.document.write(
      `
		<style>
			body { margin : 0px; }
		</style>
		<html><head><title>` +
        popWindow.document.title +
        `</title></head>
		<body>
		<iframe width="100%" height="100%" src="` +
        a.href +
        `"></iframe>
		</body>
		</html>`
    );

    popWindow.document.close();
  }

  document.body.removeChild(a);
}

export function downloadFile(
  blobContent: any,
  formatType: string,
  fileName: string,
  env: any
) {
  const mime: string = formatTypeToMime(formatType);

  const blob = new Blob([blobContent], {
    type: mime
  });
  downloadBlob(blob, fileName);
}

function downloadBlob(blob: Blob, fileName: string) {
  if (navigator.msSaveBlob) {
    // IE 10+
    navigator.msSaveBlob(blob, fileName);
  } else {
    const link = document.createElement("a");
    if (link.download !== undefined) {
      // feature detection
      // Browsers that support HTML5 download attribute
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", fileName);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }
}

export function translateMessageResult(message: MessageResult): string {
  const fields = message.fields
    ? message.fields.map(value => String(i18n.t(value))).join(" ")
    : null;
  let s = "";
  if (hasTranslation(message.code)) {
    s = String(i18n.t(message.code, message.params));
  } else {
    s = String(i18n.t(message.description, message.params));
  }
  return fields ? fields + " - " + s : s;
}

export function getCountryOrCompanyNameLabelBasedOnTenant(tenantId: number) {
  let fullName = null;
  let key = "";
  switch (tenantId) {
    case 1:
      fullName = "Sistema Italia 93 S.r.l.";
      break;
    case 2:
      fullName = "MBE SPAIN 2000 S.L.";
      break;
    case 3:
      fullName = "MBE Deutschland GmbH";
      break;
    case 4:
      key = "AT";
      break;
    case 5:
      fullName = "MBE France SARL";
      break;
    case 6:
      fullName = "MBE Poland Sp. z o.o.";
      break;
    case 7:
      fullName = "MBE SPAIN 2000 S.L.";
      break;
    case 8:
      key = "CH";
      break;
    case 9:
      key = "HR";
      break;
    case 10:
      key = "US";
      break;
    case 11:
      key = "CA";
      break;

    default:
      fullName = "Sistema Italia 93 S.r.l.";
      break;
  }

  return fullName ? fullName : `MBE ${key}`;
}

export function getCurrentLoginByCustomTenantId(tenantId: number) {
  const currentLogin: CurrentLogin = {
    initialized: true,
    isItaly: tenantId === 1,
    isSpain: tenantId === 2,
    isGermany: tenantId === 3,
    isAustria: tenantId === 4,
    isFrance: tenantId === 5,
    isPoland: tenantId === 6,
    isPortugal: tenantId === 7,
    isSwitzerland: tenantId === 8,
    isCroatia: tenantId === 9
  };
  return currentLogin;
}

export function displayPdf(
  pdf: any,
  printTitle: string,
  env: any,
  filename?: string
) {
  // used to open base64 binary pdf in popup with download option etc.
  popupFileBase64(pdf, "PDF", filename ? filename : printTitle + ".pdf", env);
}

export function downloadFileBase64(
  base64: any,
  formatType: string,
  fileName: string,
  env: any
) {
  const mime: string = formatTypeToMime(formatType);

  const blob = convertBase64toBlob(base64, mime);

  downloadBlob(blob, fileName);
}

export function getShipmentType(macroAreaFrom: string, macroAreaTo: string) {
  if (macroAreaFrom === "NATIONAL" && macroAreaTo === "NATIONAL") {
    return "DOMESTIC";
  }

  if (macroAreaFrom === "NATIONAL" && macroAreaTo !== "NATIONAL") {
    return "EXPORT";
  }

  if (macroAreaFrom !== "NATIONAL" && macroAreaTo === "NATIONAL") {
    return "IMPORT";
  }

  if (macroAreaFrom !== "NATIONAL" && macroAreaTo !== "NATIONAL") {
    return "EXPORT";
  }

  if (macroAreaFrom === "NATIONAL" && macroAreaTo === "") {
    return "DOMESTIC";
  }

  return "";
}

export function parseDateAsUTC(date: string, dateFormat?: string) {
  const [day, month, year, hours, minutes, seconds] = date
    .split(/[/: ]/)
    .map(part => parseInt(part, 10));
  const parsedDate: Date = new Date(
    year,
    month - 1,
    day,
    hours,
    minutes,
    seconds
  );
  const formattedDate: string = format(
    parsedDate,
    dateFormat ? dateFormat : "dd/MM/yyyy HH:mm:ss"
  );
  return formattedDate;
}

export function pageToPageRequest(page: any): any {
  return {
    number: page.number === 0 ? 0 : page.number - 1,
    size: page.size,
    sort: page.sort
  } as any;
}

export function getDateFormat(isForDateFns?: boolean) {
  if (isForDateFns) {
    if (isGermany()) return "DD.MM.YYYY";
    return "DD/MM/YYYY";
  }
  if (isGermany()) return "dd.MM.yyyy";
  return "dd/MM/yyyy";
}

export function getStoreCompanyName(multiStore: MultiStoreDTO): string {
  let companyName = multiStore.businessName;
  const commercialName = multiStore.commercialName;
  if (
    (isSpain() || isFrance()) &&
    commercialName !== undefined &&
    commercialName !== ""
  )
    companyName = commercialName;
  return companyName;
}

export function getCustomerCommercialName(customer: any): string | undefined {
  if (
    (isSpain() || isPortugal()) &&
    customer?.commercialName != null &&
    customer.commercialName !== "" &&
    customer?.custConfiguration?.useCommercialName
  )
    return customer.commercialName;
  return undefined;
}
